import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, Chip, Modal, SearchableSelect } from '../ui'
import type { SearchableSelectOption } from '../ui'

export interface MergeEntityInfo {
  unique_id: string
  label: string
  kind?: string
}

export interface MergeModalProps {
  open: boolean
  onClose: () => void
  title: string
  description: string
  source: MergeEntityInfo
  options: SearchableSelectOption[]
  loading?: boolean
  pending: boolean
  error: Error | null
  /** Rendered under the error, e.g. a way out when the merge was refused. */
  errorAction?: ReactNode
  onConfirm: (target: string) => void
}

/** Generic "merge this entity into another of the same kind" modal.
 *  Shows a clear SOURCE → TARGET flow so the user sees which object is
 *  deleted and which object keeps the combined data. */
export function MergeModal({
  open,
  onClose,
  title,
  description,
  source,
  options,
  loading = false,
  pending,
  error,
  errorAction,
  onConfirm,
}: MergeModalProps) {
  const [target, setTarget] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    if (open) {
      setTarget('')
      setConfirmed(false)
    }
  }, [open])

  const targetOption = useMemo(
    () => options.find((o) => o.value === target),
    [options, target],
  )

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (target && confirmed) onConfirm(target)
        }}
      >
        <div className="rounded-sm border border-ostrel-line bg-ostrel-sand/40 p-3">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-danger">
                Source — deleted
              </span>
              <div className="flex min-w-0 flex-col rounded-sm border border-ostrel-danger/30 bg-ostrel-card p-2">
                <span className="truncate text-sm font-medium text-ostrel-ink">
                  {source.label}
                </span>
                <div className="flex items-center gap-2">
                  {source.kind && <Chip tone="danger" label={source.kind} />}
                  <span className="tnum truncate text-[11px] text-ostrel-ink-muted">
                    {source.unique_id}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ostrel-navy text-ostrel-light">
              <span aria-hidden="true">→</span>
            </div>

            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ok">
                Target — kept
              </span>
              <div
                className={`flex min-w-0 flex-col rounded-sm border p-2 ${
                  targetOption
                    ? 'border-ostrel-ok/30 bg-ostrel-card'
                    : 'border-dashed border-ostrel-line bg-ostrel-card'
                }`}
              >
                {targetOption ? (
                  <>
                    <span className="truncate text-sm font-medium text-ostrel-ink">
                      {targetOption.label}
                    </span>
                    <span className="tnum truncate text-[11px] text-ostrel-ink-muted">
                      {targetOption.value}
                    </span>
                  </>
                ) : (
                  <span className="truncate text-sm text-ostrel-ink-muted">
                    — select target —
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <SearchableSelect
          label={`Target ${title.replace(/^Merge /, '').toLowerCase()}`}
          value={target}
          onChange={setTarget}
          options={options}
          placeholder="— select target —"
          searchPlaceholder="Search…"
          loading={loading}
          emptyText={loading ? 'Loading…' : 'No matching target found.'}
        />

        <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">{description}</p>

        <div className="rounded-sm border border-ostrel-danger/30 bg-ostrel-danger/10 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-ostrel-danger">
            No undo
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-ostrel-ink">
            Merging deletes the source object permanently. There is no undo function.
            All of its data moves to the target above.
          </p>
          <label className="mt-2 flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 accent-ostrel-danger"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span className="text-[11px] text-ostrel-ink">
              I understand this cannot be undone and want to proceed.
            </span>
          </label>
        </div>

        {error && <p className="text-xs text-ostrel-danger">{error.message}</p>}
        {error && errorAction}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="navy" size="sm" disabled={pending || !target || !confirmed}>
            {pending ? 'Merging…' : `Merge into ${targetOption?.label ?? 'target'}`}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
